import { useState, useEffect, useContext, useMemo } from "react";
import { Form, Typography } from "antd";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import "./feedbackRequests.css";
import secureLocalStorage from "react-secure-storage";
import { GlobalStateContext } from "../../../utils/GlobalStateContext";
import { JsonToExcel } from "react-json-to-excel";
import { exportJson } from "../../../utils/export";
import CustomLoader from "../../common/customLoader";
import media from "../../../media";
import { fetchFeedbackRequestsThunk } from "../../../store/thunks/feedbackRequestsThunk";
import { feedbackRequestSurveyPreviewThunk } from "../../../store/thunks/feedbackRequestsThunk";

import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import TableContent from "../../../components/atoms/TableContent";
import moment from "moment";
import { createAccessMap, hasPermission } from "../../../utils/permissions";
import Unauthorized from "../../layouts/Unauthorized";

const FeedbackRequests = ({ children }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { globalState, setGlobalState } = useContext(GlobalStateContext);

  const currentPath = location.pathname.replace("/", "");
  const [tableForm] = Form.useForm();
  const rawAccess = useSelector((state) => state.auth.access);
  const { list, pageInfo, count, loading } = useSelector(
    (state) => state.vocFeedbackRequests,
  );

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: Number(process.env.REACT_APP_DEFAULT_PAGINATION_PAGESIZE || 50),
  });
  const shouldShowPagination = count > pagination.pageSize;
  const tablePagination = shouldShowPagination
    ? {
        current: pagination.current,
        pageSize: pagination.pageSize,
        total: count,
        showSizeChanger: true,
        pageSizeOptions: ["10", "20", "50", "100"],
        locale: { items_per_page: "" },
        onChange: (page, pageSize) => {
          setPagination({ current: page, pageSize });
        },
      }
    : false;

  const { type } = useParams();

  const [selectedRowsArray, setSelectedRowsArray] = useState([]);
  const [feedbackRequestLists, setFeedbackRequestLists] = useState([]);

  const accessMap = useMemo(() => {
    return createAccessMap(rawAccess);
  }, [rawAccess]);

  const hasViewAccess = hasPermission(
    accessMap,
    ["vOCFROpen", "vOCFRExpirvOCFRClosed", "vOCFRExpired"],
    "view",
  );
  let columns = [
    {
      title: () => {
        return (
          <div className="tableTitle no-wrap">
            <img src={media.auditId.svg} alt="icon" />
            ID
          </div>
        );
      },
      dataIndex: "id",
      render: (text) => <span className="rowStyle no-wrap">{text}</span>,
    },
    {
      title: () => {
        return (
          <div className="tableTitle no-wrap">
            <img src={media.departmentIcon.svg} alt="icon" />
            Type Of Form
          </div>
        );
      },
      dataIndex: "formType",
      render: (text) => {
        const value = text || "";
        const capitalized = value.charAt(0).toUpperCase() + value.slice(1);

        return <span className="rowStyle no-wrap">{capitalized}</span>;
      },
    },
    {
      title: () => {
        return (
          <div className="tableTitle no-wrap">
            <img src={media.agent.svg} alt="icon" />
            Recipient
          </div>
        );
      },
      dataIndex: "recipient",

      render: (text) => {
        const name = text || "";
        const firstLetter = name.charAt(0).toUpperCase();
        return (
          <span className="rowStyle no-wrap profileText">
            {text?.profilePicUrl ? (
              <img
                src={text.profilePicUrl}
                //src={media.profileIcon.svg}
                alt={name}
                className="tableProfile"
              />
            ) : (
              <span className="firstCaps firstSupervisor">{firstLetter}</span>
            )}

            {text}
          </span>
        );
      },
    },

    {
      title: () => {
        return (
          <div className="tableTitle no-wrap">
            <img src={media.departmentDate.svg} alt="icon" />
            Created On
          </div>
        );
      },
      dataIndex: "createdAt",
      render: (text) => <span className="rowStyle no-wrap">{text}</span>,
    },
    ...(hasPermission(accessMap, ["vOCFROpen", "vOCFRClosed"], "VIEWSURVEYLINK")
      ? [
          {
            title: () => {
              return (
                <div className="tableTitle no-wrap">
                  <img src={media.link.svg} alt="icon" />
                  Feedback Link
                </div>
              );
            },
            dataIndex: "feedBackLink",
            render: (text) => (
              <span className="rowStyle no-wrap">
                <Typography.Link
                  href={text}
                  target="_blank"
                  className="tableBtn bfont"
                >
                  <img src={media.link.svg} alt="icon" /> Link
                </Typography.Link>
              </span>
            ),
          },
        ]
      : []),
    {
      title: () => {
        return (
          <div className="tableTitle no-wrap">
            <img src={media.departmentStatus.svg} alt="icon" />
            Status
          </div>
        );
      },
      dataIndex: "status",
      render: (text) => (
        <span
          className={`rowStyle no-wrap cusStatus ${
            text?.toLowerCase() === "open" ? "cusActive" : "cusInActive"
          }`}
        >
          {text}
        </span>
      ),
    },
  ];

  if (location.pathname.includes("/closed")) {
    columns.splice(5, 0, {
      title: () => {
        return (
          <div className="tableTitle  no-wrap">
            <img src={media.agent.svg} alt="icon" />
            Captured On
          </div>
        );
      },
      dataIndex: "capturedOn",
      render: (text) => <span className="rowStyle no-wrap">{text}</span>,
    });
    if (
      hasPermission(
        accessMap,
        ["vOCFROpen", "vOCFRClosed", "vOCFRExpired"],
        "PREVIEW",
      )
    ) {
      columns.push({
        title: () => {
          return (
            <div className="tableTitle  no-wrap">
              <img src={media.link.svg} alt="icon" />
              Preview
            </div>
          );
        },
        dataIndex: "previewBy",
        render: (text) => (
          <span className="rowstyle previewBy">
            {text ? (
              <span
                className="tableBtn bfont"
                onClick={() => handlePreview(text)}
              >
                <img src={media.link.svg} alt="icon" /> View
              </span>
            ) : (
              ""
            )}
          </span>
        ),
      });
    }
  }

  const handlePreview = async (id) => {
    const previewDetails = {};
    previewDetails.previewedBy = secureLocalStorage.getItem("lIUId");
    previewDetails.previewedAt = new Date(
      new Date().getTime() - new Date().getTimezoneOffset() * 60000,
    )
      .toISOString()
      .slice(0, 19)
      .replace("T", " ");

    const payload = {
      fid: id,
      previewDetails: previewDetails && JSON.stringify(previewDetails),
    };

    await dispatch(feedbackRequestSurveyPreviewThunk(payload));
    navigate("/feedback-requests/preview/" + id, { replace: true });
  };
  const [selectionType, setSelectionType] = useState("checkbox");

  // Fetch Feedback Requests List
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Reset selections
        setSelectedRowKeys([]);
        setSelectedRowsArray([]);

        // Search flags
        const hasSearchedValue = secureLocalStorage.getItem("hasSearchedValue")
          ? "yes"
          : "";

        const searchIn = location.pathname.split("/")[1];
        const type = location.pathname.split("/")[2];
        const res = await dispatch(
          fetchFeedbackRequestsThunk({
            searchIn,
            type,
            callActionFrom: type,
            route: currentPath,

            hasSearchedValue,
            searchKeyword: type,

            pagination: {
              current: pagination.current || 1,
              page: pagination.current || 1,
              pageSize: pagination.pageSize || 100,
            },

            selectedRowsArray,
            setSelectedRowsArray,
            setPagination,
          }),
        ).unwrap();
        if (res?.meta?.feedbackRequestLists) {
          setFeedbackRequestLists(res.meta.feedbackRequestLists);
        }
        const sent = Number(res?.meta?.sent ?? 0);
        const reverted = Number(res?.meta?.reverted ?? 0);

        setGlobalState((prev) => ({
          ...prev,
          sent,
          reverted,
          notReverted: Math.max(sent - reverted, 0),
        }));
      } catch (error) {
        console.error("Error fetching feedback requests:", error);
      }
    };

    fetchData();
  }, [type, pagination.current, pagination.pageSize, dispatch]);

  useEffect(() => {
    // Clear table selection
    setSelectedRowKeys([]);
    setSelectedRowsArray([]);

    // Clear global export state
    setGlobalState((prev) => ({
      ...prev,
      selectedRowKeysChildInfo: [],
      selectedRowKeys: [],
      exportFeedbackRequestsJson: [],
    }));
  }, [location.pathname]);
  {
    /*Export functionality on VOC Form Listing's table clicking on checkboxes*/
  }
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  const tableLoading = loading; // Redux loading
  const summaryReady =
    globalState.sent !== undefined &&
    globalState.reverted !== undefined &&
    globalState.notReverted !== undefined;

  const showPageLoader = tableLoading || !summaryReady;
  const exportColumns = [
    { key: "Type of form", path: "form.formType" },
    { key: "Recipient", path: "recipient" },
    { key: "Status", path: "status" },
    {
      key: "Created At",
      path: "createdAt",
      formatter: (value) =>
        value ? moment(value).format("YYYY-MM-DD HH:mm A") : "",
    },
    {
      key: "Captured On",
      path: "capturedOn",
      formatter: (value) =>
        value ? moment(value).format("YYYY-MM-DD HH:mm A") : "",
    },
    {
      key: "Reminder Send On",
      path: "reminderSentOn",
      formatter: (value) =>
        value ? moment(value).format("YYYY-MM-DD HH:mm A") : "",
    },
    {
      key: "FeedBack Link",
      formatter: (feedbackRequestLists) =>
        `${process.env.REACT_APP_URI}/survey/${feedbackRequestLists._id}`,
    },
    {
      key: "Score",
      path: "score",
    },
  ];

  const rowSelection = {
    type: "checkbox",
    selectedRowKeys,
    onChange: (selectedRowKeys, selectedRows) => {
      setSelectedRowKeys(selectedRowKeys);

      setGlobalState((prev) => ({
        ...prev,
        selectedRowKeysChildInfo: selectedRowKeys,
        selectedRowKeys: selectedRows.map((row) => row.id),
        exportFeedbackRequestsJson: selectedRows,
      }));

      exportJson(
        selectedRows,
        feedbackRequestLists,
        "FeedbackRequests",
        exportColumns,
        setGlobalState,
        globalState,
      );
    },
  };
  if (!hasViewAccess) {
    return <Unauthorized />;
  }
  return (
    <>
      <div className="all-content">
        <div className="feedback-boxes">
          <div className="feedbackboxInner">
            <div className="feedback-box sentFeedback">
              <h4>{globalState?.sent}</h4>
              <span className="smallLightText">Sent Feedback</span>
            </div>
            <div className="feedback-box revertFeedback">
              <h4>{globalState?.reverted}</h4>
              <span className="smallLightText">Reverted Feedback</span>
            </div>
            <div className="feedback-box noFeedback">
              <h4>{globalState?.notReverted}</h4>
              <span className="smallLightText">Not Reverted Feedback</span>
            </div>
          </div>
          <div className="feedbackboxInnerBtn">
            {(secureLocalStorage.getItem("role") === "admin" ||
              secureLocalStorage.getItem("role") === "qc") &&
              Array.isArray(globalState.selectedRowKeysChildInfo) &&
              globalState.selectedRowKeysChildInfo.length > 0 &&
              hasPermission(
                accessMap,
                ["vOCFROpen", "vOCFRExpirvOCFRClosed", "vOCFRExpired"],
                "DOWNLOADASEXCEL",
              ) && (
                <JsonToExcel
                  title="Download as Excel"
                  data={globalState.exportFeedbackRequestsJson}
                  fileName="feedbackRequest"
                  btnClassName="defaultBtn defaultGreyBtn"
                />
              )}
          </div>
        </div>
        <div className="all-table-holder">
          <CustomLoader showLoader={showPageLoader} />

          <Form form={tableForm} component={false}>
            <TableContent
              rowSelection={rowSelection}
              bordered={false}
              columns={columns}
              dataSource={list}
              pagination={tablePagination}
            />
          </Form>
        </div>
      </div>
    </>
  );
};
export default FeedbackRequests;
